import React, { useCallback, useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Select, Space, Typography, Tag, App } from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useAuth } from '../context/AuthContext';
import { bundlesApi } from '../api/bundles';
import { modulesApi } from '../api/modules';
import { Bundle, BundleStatus, CourseModule } from '../api/types';

const { Title } = Typography;

const STATUS_COLORS: Record<BundleStatus, string> = {
  Enabled: 'success',
  Disabled: 'warning',
  Test: 'processing',
};

const STATUS_OPTIONS: BundleStatus[] = ['Enabled', 'Disabled', 'Test'];

interface BundleFormValues {
  title: string;
  description?: string | null;
  imageUrl?: string | null;
  moduleIds: number[];
  discountPercent: number;
  status: BundleStatus;
  sortOrder: number;
}

const BundlesPage: React.FC = () => {
  const { apiClient } = useAuth();
  const { message } = App.useApp();

  const [bundles, setBundles] = useState<Bundle[]>([]);
  const [modules, setModules] = useState<CourseModule[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Bundle | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm<BundleFormValues>();

  const bApi = useCallback(() => bundlesApi(apiClient!), [apiClient]);
  const mApi = useCallback(() => modulesApi(apiClient!), [apiClient]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [bundlesRes, modulesRes] = await Promise.all([bApi().getList(), mApi().getAll()]);
      setBundles(bundlesRes.data.items);
      setModules(modulesRes.data.items);
    } catch {
      message.error('Failed to load bundles');
    } finally {
      setLoading(false);
    }
  }, [bApi, mApi, message]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditing(null);
    form.setFieldsValue({
      title: '', description: '', imageUrl: '',
      moduleIds: [], discountPercent: 0,
      status: 'Disabled' as BundleStatus,
      sortOrder: bundles.length,
    });
    setModalOpen(true);
  };

  const openEdit = (b: Bundle) => {
    setEditing(b);
    form.setFieldsValue({
      title: b.title, description: b.description ?? '', imageUrl: b.imageUrl ?? '',
      moduleIds: b.moduleIds, discountPercent: b.discountPercent,
      status: b.status, sortOrder: b.sortOrder,
    });
    setModalOpen(true);
  };

  const handleSave = async (values: BundleFormValues) => {
    setSaving(true);
    try {
      const payload = {
        title: values.title,
        description: values.description || null,
        imageUrl: values.imageUrl || null,
        moduleIds: values.moduleIds,
        discountPercent: values.discountPercent,
        status: values.status,
        sortOrder: values.sortOrder,
      };
      if (editing) {
        await bApi().update({ ...payload, id: editing.id });
        message.success('Bundle updated');
      } else {
        await bApi().create(payload);
        message.success('Bundle created');
      }
      setModalOpen(false);
      await fetchData();
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Failed to save bundle';
      message.error(msg);
    } finally {
      setSaving(false);
    }
  };

  // Build a "Course / Module title" map for display
  const moduleOptions = modules.map((m) => ({
    value: m.id,
    label: `[${m.courseId}] ${m.title}`,
  }));

  const columns: ColumnsType<Bundle> = [
    { title: 'Id', dataIndex: 'id', width: 70 },
    { title: 'Title', dataIndex: 'title' },
    {
      title: 'Modules',
      dataIndex: 'moduleIds',
      render: (ids: number[]) => `${ids.length}`,
    },
    {
      title: 'Discount',
      dataIndex: 'discountPercent',
      render: (v: number) => v > 0 ? `−${v}%` : '—',
      width: 100,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (s: BundleStatus) => <Tag color={STATUS_COLORS[s]}>{s}</Tag>,
      width: 110,
    },
    { title: 'Order', dataIndex: 'sortOrder', width: 80 },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, b) => (
        <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(b)}>Edit</Button>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Bundles</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>New Bundle</Button>
      </Space>

      <Table rowKey="id" loading={loading} dataSource={bundles} columns={columns} pagination={false} />

      <Modal
        open={modalOpen}
        title={editing ? `Edit Bundle #${editing.id}` : 'New Bundle'}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={saving}
        width={720}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item label="Title" name="title" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item label="Image URL" name="imageUrl">
            <Input />
          </Form.Item>
          <Form.Item
            label="Modules (order matters)"
            name="moduleIds"
            rules={[{ required: true, message: 'Select at least one module' }]}
          >
            <Select
              mode="multiple"
              placeholder="Select modules"
              options={moduleOptions}
              optionFilterProp="label"
              showSearch
            />
          </Form.Item>
          <Form.Item label="Discount (%)" name="discountPercent" rules={[{ required: true, type: 'number', min: 0, max: 99 }]}>
            <InputNumber min={0} max={99} style={{ width: 200 }} />
          </Form.Item>
          <Form.Item label="Status" name="status" rules={[{ required: true }]}>
            <Select options={STATUS_OPTIONS.map((s) => ({ value: s, label: s }))} />
          </Form.Item>
          <Form.Item label="Sort Order" name="sortOrder" rules={[{ required: true, type: 'number' }]}>
            <InputNumber min={0} style={{ width: 200 }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BundlesPage;
